const CHUNK_RELOAD_KEY = 'growzz-chunk-reload';

const STALE_CHUNK_MESSAGES = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'ChunkLoadError',
];

export interface ChunkReloadStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface ChunkReloadHost {
  sessionStorage: ChunkReloadStorage;
  location: { reload(): void };
  addEventListener(
    type: 'unhandledrejection',
    listener: (event: PromiseRejectionEvent) => void,
  ): void;
}

export function isStaleChunkLoadError(reason: unknown): boolean {
  const message = reasonMessage(reason);
  return STALE_CHUNK_MESSAGES.some((fragment) => message.includes(fragment));
}

export function reloadOnceForStaleChunk(
  reason: unknown,
  storage: ChunkReloadStorage,
  reload: () => void,
): boolean {
  if (!isStaleChunkLoadError(reason)) {
    return false;
  }
  if (storage.getItem(CHUNK_RELOAD_KEY) === '1') {
    return false;
  }
  storage.setItem(CHUNK_RELOAD_KEY, '1');
  reload();
  return true;
}

export function clearChunkReloadGuard(storage: ChunkReloadStorage): void {
  storage.removeItem(CHUNK_RELOAD_KEY);
}

export function installChunkLoadRecovery(host: ChunkReloadHost): void {
  host.addEventListener('unhandledrejection', (event) => {
    if (recoverStaleChunk(event.reason, host)) {
      event.preventDefault();
    }
  });
}

export function recoverStaleChunk(reason: unknown, host: ChunkReloadHost): boolean {
  const storage = storageOf(host);
  if (storage === null) {
    return false;
  }
  return reloadOnceForStaleChunk(reason, storage, () => {
    host.location.reload();
  });
}

export function markChunkReloadRecovered(host: ChunkReloadHost): void {
  const storage = storageOf(host);
  if (storage !== null) {
    clearChunkReloadGuard(storage);
  }
}

function storageOf(host: ChunkReloadHost): ChunkReloadStorage | null {
  try {
    return host.sessionStorage;
  } catch (error: unknown) {
    void error;
    return null;
  }
}

function reasonMessage(reason: unknown): string {
  if (typeof reason === 'string') {
    return reason;
  }
  if (reason instanceof Error) {
    return `${reason.name} ${reason.message}`;
  }
  return '';
}
